import { Injectable } from '@angular/core';

import { sucursalesDe } from '../data/dimensions.data';
import { INpsSummary } from '../models/nps-summary.model';
import { ISeriesPoint } from '../models/series-point.model';
import { hashTexto, limitar, normalizar, pseudoAleatorio } from '../models/deterministic.model';
import { formatRangeLabel, monthsInRange, parseRange } from '../models/date-range.model';
import { FilterField } from '../models/filter-option.model';

/* -------------------------------------------------------------------------- */
/*                              ANCHOR universo                               */
/* -------------------------------------------------------------------------- */

/** Último mes con datos en el tablero. Todas las series terminan aquí. */
export const MES_FINAL = '2026-08';

/** Primer mes con datos (`ETIQUETAS_HISTORICO` arranca en Feb-25). */
export const MES_INICIAL = '2025-02';

const NOMBRES_MES: ReadonlyArray<string> = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/**
 * Cuántos puntos conserva una serie cuando el periodo elegido es un solo mes.
 * Trece, que es la ventana que el tablero anuncia («los últimos trece meses»):
 * con la selección por omisión —Agosto/2026— las gráficas quedan intactas.
 */
const VENTANA_MINIMA = 13;

/** Campos de la selección que sitúan a la sucursal en la jerarquía. */
const CAMPOS_ENTIDAD = ['region', 'zona', 'sucursal', 'estado'] as const;

/** Campos que acotan el periodo. */
const CAMPOS_TIEMPO = ['anio', 'mes', 'rango'] as const;

/* -------------------------------------------------------------------------- */
/*                              ANCHOR contexto                               */
/* -------------------------------------------------------------------------- */

/** La selección de filtros, ya resuelta a algo que se puede aplicar. */
export interface IFilterContext {
  /** Hay al menos un filtro puesto. Con `false` todo el servicio es identidad. */
  activo: boolean;

  /** Meses `YYYY-MM` que abarca el periodo elegido, en orden. */
  meses: ReadonlyArray<string>;
  tiempoActivo: boolean;

  /** Último mes del periodo: el que rotulan los KPIs. */
  mesReferencia: string;

  /** Sucursales (normalizadas) que sobreviven al filtro; null = todas. */
  sucursales: ReadonlySet<string> | null;
  entidadActiva: boolean;

  /** Estados elegidos, tal cual vienen de la selección. */
  estados: ReadonlyArray<string>;

  /** 'Comerciante' | 'Consumidor final' cuando se eligió uno solo. */
  tipoCliente: string | null;

  /** Semilla de las cifras derivadas: misma selección, mismos números. */
  semilla: number;
}

const CONTEXTO_VACIO: IFilterContext = {
  activo: false,
  meses: [],
  tiempoActivo: false,
  mesReferencia: MES_FINAL,
  sucursales: null,
  entidadActiva: false,
  estados: [],
  tipoCliente: null,
  semilla: 0,
};

/**
 * Traduce la selección de `<app-filter-bar>` a datos filtrados.
 *
 * Hay dos maneras de responder a un filtro, y el servicio usa cada una donde
 * corresponde:
 *
 *  - **Filtrado real.** Cuando la tabla trae la dimensión —o se puede llegar a
 *    ella por el catálogo de `dimensions.data.ts`— se quitan renglones de
 *    verdad: alertas, planes, comentarios, NPS por sucursal, ranking, CSAT por
 *    tienda, detalle por estado, ranking por región. Lo mismo con el tiempo:
 *    un rango de fechas recorta las series a los meses que abarca.
 *
 *  - **Derivación determinista.** Los agregados que el original publica sin
 *    ninguna dimensión —drivers de recomendación, palancas, CSAT por variable,
 *    monto de compra, embudo de entregabilidad— no se pueden recalcular: no
 *    existe el renglón por encuesta del que saldrían. Ahí la cifra se desplaza
 *    con un generador sembrado por la selección: se mueve de forma coherente,
 *    da siempre el mismo resultado para el mismo filtro, y **con la selección
 *    vacía devuelve exactamente el número original**.
 *
 * Esa última parte es dato sintético, igual que las sucursales generadas de
 * `results.data.ts`. Es lo que permite que la demo se comporte como el producto
 * sin inventarse un backend; no es una cifra del programa.
 */
@Injectable({ providedIn: 'root' })
export class FilterService {

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR contexto                               */
  /* -------------------------------------------------------------------------- */

  public contexto(seleccion: Record<string, Array<string>> | null | undefined): IFilterContext {
    const activos = this.compactar(seleccion);

    if (!Object.keys(activos).length) {
      return CONTEXTO_VACIO;
    }

    const meses = this.resolverMeses(activos);
    const tiempoActivo = CAMPOS_TIEMPO.some((campo) => (activos[campo] ?? []).length > 0);
    const entidadActiva = CAMPOS_ENTIDAD.some((campo) => (activos[campo] ?? []).length > 0);

    const tipos = activos['tipoCliente'] ?? [];

    return {
      activo: true,
      meses,
      tiempoActivo,
      mesReferencia: meses[meses.length - 1] ?? MES_FINAL,
      sucursales: entidadActiva ? this.resolverSucursales(activos) : null,
      entidadActiva,
      estados: activos['estado'] ?? [],
      tipoCliente: tipos.length === 1 ? tipos[0] : null,
      semilla: hashTexto(JSON.stringify(activos)),
    };
  }

  /** Copia la selección sin los campos vacíos y con los valores ordenados,
   *  para que dos selecciones equivalentes den la misma semilla. */
  private compactar(
    seleccion: Record<string, Array<string>> | null | undefined,
  ): Record<string, Array<string>> {
    const resultado: Record<string, Array<string>> = {};

    for (const clave of Object.keys(seleccion ?? {}).sort()) {
      const valores = seleccion![clave];

      if (valores?.length) {
        resultado[clave] = [...valores].sort();
      }
    }

    return resultado;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR tiempo                                */
  /* -------------------------------------------------------------------------- */

  /**
   * Meses que cubre la selección.
   *
   * El rango de fechas manda sobre año/mes: es la forma precisa de decir lo
   * mismo, y `<app-filter-bar>` ya impide que convivan. Sin ninguno de los
   * tres, el periodo es todo el universo de datos.
   */
  private resolverMeses(activos: Record<string, Array<string>>): Array<string> {
    const rango = parseRange((activos['rango'] ?? [])[0]);

    if (rango) {
      const meses = monthsInRange(rango).filter((mes) => mes >= MES_INICIAL && mes <= MES_FINAL);

      return meses.length ? meses : [MES_FINAL];
    }

    const anios = activos['anio'] ?? [];
    const nombres = activos['mes'] ?? [];

    if (!anios.length && !nombres.length) {
      return this.universo();
    }

    const indices = nombres
      .map((nombre) => NOMBRES_MES.findIndex((mes) => normalizar(mes) === normalizar(nombre)))
      .filter((indice) => indice >= 0);

    return this.universo().filter((mes) => {
      const [anio, numero] = mes.split('-');

      if (anios.length && !anios.includes(anio)) {
        return false;
      }

      return !indices.length || indices.includes(Number(numero) - 1);
    });
  }

  /** Todos los meses con datos, de `MES_INICIAL` a `MES_FINAL`. */
  private universo(): Array<string> {
    const meses: Array<string> = [];
    const [anioFin, mesFin] = MES_FINAL.split('-').map(Number);
    let [anio, mes] = MES_INICIAL.split('-').map(Number);

    while (anio < anioFin || (anio === anioFin && mes <= mesFin)) {
      meses.push(`${anio}-${`${mes}`.padStart(2, '0')}`);

      if (++mes > 12) {
        mes = 1;
        anio++;
      }
    }

    return meses;
  }

  /**
   * Recorta una serie a los meses del periodo.
   *
   * Las series del tablero son consecutivas y terminan en `MES_FINAL`, así que
   * la posición basta para saber a qué mes corresponde cada punto: no hace
   * falta interpretar las tres formas distintas en que los datos rotulan el mes
   * (`Ago-26`, `Agosto`, `Ago-25`).
   *
   * Cuando el periodo es un solo mes —el caso por omisión: Agosto/2026— recortar
   * dejaría un punto suelto y la tendencia dejaría de leerse. En ese caso se
   * conserva la ventana de doce meses que **termina** en el mes elegido, que es
   * lo que hace cualquier tablero de NPS: el periodo rotula el KPI, no amputa
   * el histórico.
   */
  public serie(
    puntos: ReadonlyArray<ISeriesPoint>,
    ctx: IFilterContext,
    clave = 'serie',
  ): Array<ISeriesPoint> {
    return this.derivarSerie(this.recortar(puntos, ctx), ctx, clave);
  }

  /**
   * Igual que `serie()`, para las series cuyo valor es un conteo y no un
   * porcentaje (detractores del mes, envíos por canal). Aquí el valor no se
   * desplaza: se escala con el universo, porque acotar a una región no cambia
   * el nivel de la métrica, cambia cuántos casos hay.
   */
  public serieConteo(
    puntos: ReadonlyArray<ISeriesPoint>,
    ctx: IFilterContext,
  ): Array<ISeriesPoint> {
    const recortada = this.recortar(puntos, ctx);

    if (!this.deriva(ctx)) {
      return [...recortada];
    }

    const factor = this.factorMuestra(ctx);

    return recortada.map((punto) => ({
      ...punto,
      value: Math.max(0, Math.round(punto.value * factor)),
      count: punto.count === undefined ? undefined : Math.max(1, Math.round(punto.count * factor)),
    }));
  }

  private recortar(
    puntos: ReadonlyArray<ISeriesPoint>,
    ctx: IFilterContext,
  ): ReadonlyArray<ISeriesPoint> {
    if (!ctx.activo || !puntos.length) {
      return puntos;
    }

    const meses = this.mesesDe(puntos.length);
    const seleccionados = new Set(ctx.meses);
    const dentro = puntos.filter((_, i) => seleccionados.has(meses[i]));

    if (dentro.length >= 2) {
      return dentro;
    }

    const fin = meses.findIndex((mes) => mes === ctx.mesReferencia);
    const corte = fin >= 0 ? fin + 1 : puntos.length;

    return puntos.slice(Math.max(0, corte - VENTANA_MINIMA), corte);
  }

  /** Meses que ocupa una serie de `longitud` puntos terminada en `MES_FINAL`. */
  private mesesDe(longitud: number): Array<string> {
    const [anioFin, mesFin] = MES_FINAL.split('-').map(Number);
    const meses: Array<string> = [];

    for (let i = longitud - 1; i >= 0; i--) {
      const total = anioFin * 12 + (mesFin - 1) - i;
      const anio = Math.floor(total / 12);
      const mes = (total % 12) + 1;

      meses.push(`${anio}-${`${mes}`.padStart(2, '0')}`);
    }

    return meses;
  }

  /** Desplaza los valores de una serie cuando el filtro no es sólo de tiempo. */
  private derivarSerie(
    puntos: ReadonlyArray<ISeriesPoint>,
    ctx: IFilterContext,
    clave: string,
  ): Array<ISeriesPoint> {
    if (!this.deriva(ctx)) {
      return [...puntos];
    }

    return puntos.map((punto, i) => ({
      ...punto,
      value: this.desviar(punto.value, ctx, `${clave}:${i}`, 6, 0, 100),
      count: punto.count === undefined
        ? undefined
        : Math.max(1, Math.round(punto.count * this.factorMuestra(ctx))),
    }));
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR entidades                              */
  /* -------------------------------------------------------------------------- */

  private resolverSucursales(activos: Record<string, Array<string>>): Set<string> {
    const explicitas = (activos['sucursal'] ?? []).map(normalizar);
    const jerarquia = sucursalesDe({
      regiones: activos['region'],
      zonas: activos['zona'],
      estados: activos['estado'],
    });

    if (!explicitas.length) {
      return jerarquia;
    }

    const hayJerarquia = ['region', 'zona', 'estado'].some((campo) => (activos[campo] ?? []).length);

    if (!hayJerarquia) {
      return new Set(explicitas);
    }

    // Con jerarquía y sucursal a la vez, la sucursal acota dentro de la
    // jerarquía; una sucursal de otra región no debe reaparecer.
    return new Set(explicitas.filter((nombre) => jerarquia.has(nombre)));
  }

  /**
   * Deja las filas que casan con TODOS los campos con selección.
   *
   * Sin criterios devuelve el arreglo original —no una copia— para no relanzar
   * el `ngOnChanges` de `<app-data-table>` y perder la página en la que iba el
   * usuario.
   */
  public filas<T>(
    filas: Array<T>,
    seleccion: Record<string, Array<string>>,
    campos: ReadonlyArray<Extract<keyof T, string>>,
  ): Array<T> {
    const criterios = campos
      .map((campo) => [campo, (seleccion?.[campo] ?? []).map(normalizar)] as const)
      .filter(([, valores]) => valores.length > 0);

    if (!criterios.length) {
      return filas;
    }

    return filas.filter((fila) =>
      criterios.every(([campo, valores]) => valores.includes(normalizar(String(fila[campo])))));
  }

  /**
   * Filtra por sucursal cruzando con el catálogo de dimensiones: sirve para las
   * tablas que sólo traen el nombre de la tienda (NPS por sucursal, ranking,
   * CSAT por tienda).
   */
  public porSucursal<T>(
    filas: Array<T>,
    ctx: IFilterContext,
    campo: Extract<keyof T, string>,
  ): Array<T> {
    if (!ctx.sucursales) {
      return filas;
    }

    const permitidas = ctx.sucursales;

    return filas.filter((fila) => permitidas.has(normalizar(String(fila[campo]))));
  }

  /**
   * Filtra por el nombre que va en la columna, contra los valores elegidos de
   * varios campos a la vez. Es lo que necesita el ranking por región, cuya
   * única columna descriptiva mezcla región, zona y sucursal.
   */
  public porNombre<T>(
    filas: Array<T>,
    seleccion: Record<string, Array<string>>,
    campo: Extract<keyof T, string>,
    campos: ReadonlyArray<string>,
  ): Array<T> {
    const permitidos = new Set(
      campos.flatMap((clave) => (seleccion?.[clave] ?? []).map(normalizar)),
    );

    if (!permitidos.size) {
      return filas;
    }

    return filas.filter((fila) => permitidos.has(normalizar(String(fila[campo]))));
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR derivación                              */
  /* -------------------------------------------------------------------------- */

  /**
   * Sólo se derivan cifras cuando el filtro dice algo sobre QUIÉN o QUÉ se está
   * midiendo. Un filtro de puro tiempo ya se aplicó recortando la serie: mover
   * además los valores sería contar dos veces el mismo criterio.
   */
  public deriva(ctx: IFilterContext): boolean {
    return ctx.entidadActiva || ctx.tipoCliente !== null;
  }

  /**
   * Desplaza un valor de forma estable. Misma selección y misma clave → mismo
   * desplazamiento, en cada carga y en cada equipo.
   */
  public desviar(
    base: number,
    ctx: IFilterContext,
    clave: string,
    amplitud = 6,
    min = 0,
    max = 100,
  ): number {
    if (!this.deriva(ctx)) {
      return base;
    }

    const rnd = pseudoAleatorio(ctx.semilla ^ hashTexto(clave));
    const delta = (rnd() * 2 - 1) * amplitud;

    return limitar(Math.round(base + delta), min, max);
  }

  /** Igual que `desviar`, conservando decimales (porcentajes con dos cifras). */
  public desviarDecimal(
    base: number,
    ctx: IFilterContext,
    clave: string,
    amplitud = 4,
    min = 0,
    max = 100,
  ): number {
    if (!this.deriva(ctx)) {
      return base;
    }

    const rnd = pseudoAleatorio(ctx.semilla ^ hashTexto(clave));
    const delta = (rnd() * 2 - 1) * amplitud;

    return Math.round(limitar(base + delta, min, max) * 100) / 100;
  }

  /**
   * Cuánto se encoge la muestra al acotar el universo. Es una proporción, no un
   * conteo real: las tablas de encuestas por sucursal no existen en la demo.
   */
  public factorMuestra(ctx: IFilterContext): number {
    if (!ctx.activo) {
      return 1;
    }

    let factor = 1;

    if (ctx.sucursales) {
      // El catálogo ronda las 150 sucursales; la proporción sale de ahí.
      factor *= limitar(ctx.sucursales.size / 150, 0.02, 1);
    }

    if (ctx.tipoCliente) {
      factor *= ctx.tipoCliente === 'Comerciante' ? 0.34 : 0.66;
    }

    return factor;
  }

  /**
   * Desglose NPS derivado.
   *
   * Se mueven detractores y pasivos, y de ahí salen promotores y neto: así el
   * bloque sigue sumando 100 y `nps = promotores - detractores`, que es la
   * definición. Derivar los cuatro por separado produciría tarjetas que no
   * cuadran consigo mismas.
   */
  public resumen(base: INpsSummary, ctx: IFilterContext, clave: string): INpsSummary {
    if (!this.deriva(ctx)) {
      return base;
    }

    const detractor = this.desviar(base.detractor, ctx, `${clave}:det`, 4, 1, 30);
    const pasivo = this.desviar(base.pasivo, ctx, `${clave}:pas`, 5, 5, 40);
    const promotor = 100 - detractor - pasivo;

    return {
      ...base,
      detractor,
      pasivo,
      promotor,
      nps: promotor - detractor,
    };
  }

  /**
   * Reparte cien puntos entre categorías. Mantiene el orden de magnitud de cada
   * una y corrige el redondeo en la mayor, para que la suma siga siendo 100.
   */
  public reparto(
    valores: ReadonlyArray<number>,
    ctx: IFilterContext,
    clave: string,
  ): Array<number> {
    if (!this.deriva(ctx) || !valores.length) {
      return [...valores];
    }

    const rnd = pseudoAleatorio(ctx.semilla ^ hashTexto(clave));
    const pesos = valores.map((valor) => Math.max(0, valor * (0.7 + rnd() * 0.6)));
    const total = pesos.reduce((suma, peso) => suma + peso, 0);

    if (total <= 0) {
      return [...valores];
    }

    const escala = valores.reduce((suma, valor) => suma + valor, 0) / total;
    const crudo = pesos.map((peso) => Math.round(peso * escala));
    const diferencia = valores.reduce((suma, valor) => suma + valor, 0)
      - crudo.reduce((suma, valor) => suma + valor, 0);
    const mayor = crudo.indexOf(Math.max(...crudo));

    crudo[mayor] = Math.max(0, crudo[mayor] + diferencia);

    return crudo;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR etiquetas                             */
  /* -------------------------------------------------------------------------- */

  /** «Agosto/2026» o «Feb/2026 – Ago/2026»: qué periodo se está mirando. */
  public etiquetaPeriodo(ctx: IFilterContext, porOmision: string): string {
    if (!ctx.tiempoActivo || !ctx.meses.length) {
      return porOmision;
    }

    const primero = this.nombreMes(ctx.meses[0]);
    const ultimo = this.nombreMes(ctx.meses[ctx.meses.length - 1]);

    return primero === ultimo ? primero : `${primero} – ${ultimo}`;
  }

  private nombreMes(clave: string): string {
    const [anio, mes] = clave.split('-');

    return `${NOMBRES_MES[Number(mes) - 1]}/${anio}`;
  }

  /**
   * La selección escrita como la leería una persona:
   * «Año: 2026 · Mes: Agosto · Estado: HIDALGO».
   *
   * Va en la cabecera de lo que se exporta. Un CSV sin esta línea es una tabla
   * de cifras que nadie puede volver a situar dentro de tres semanas.
   */
  public descripcion(
    seleccion: Record<string, Array<string>> | null | undefined,
    campos: ReadonlyArray<FilterField>,
  ): string {
    const partes: Array<string> = [];

    for (const campo of campos) {
      const valores = seleccion?.[campo.key] ?? [];

      if (!valores.length) {
        continue;
      }

      const textos = valores.map((valor) => campo.type === 'range'
        ? (formatRangeLabel(valor) || valor)
        : (campo.options.find((opcion) => opcion.value === valor)?.label ?? valor));

      partes.push(`${campo.label}: ${textos.join(', ')}`);
    }

    return partes.join(' · ');
  }

  /** Frase corta con lo que está acotando la vista; vacía si no hay filtros. */
  public resumenAlcance(ctx: IFilterContext): string {
    if (!ctx.activo) {
      return '';
    }

    const partes: Array<string> = [];

    if (ctx.sucursales) {
      const total = ctx.sucursales.size;
      partes.push(total === 1 ? '1 sucursal' : `${total} sucursales`);
    }

    if (ctx.tipoCliente) {
      partes.push(ctx.tipoCliente.toLowerCase());
    }

    return partes.join(' · ');
  }
}
